/**
 * PortalUserSelector.jsx
 * Shows a user search/select UI when an admin accesses a personal portal
 * (student, teacher, corporate) without having that role.
 * Allows selecting a user to impersonate.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Input, Button, Message, Spin, Empty, Space, Tag, Typography, List, Avatar,
} from '@arco-design/web-react';
import { IconSearch, IconEye, IconUser } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@/modules/auth/useAuth';
import UsersAPI from '@/modules/users/services/usersAPI';

const PORTAL_CONFIG = {
  student: { dashboardPath: '/alumno/dashboard', color: 'cyan' },
  teacher: { dashboardPath: '/docente/dashboard', color: 'arcoblue' },
  corporate: { dashboardPath: '/corporacion/dashboard', color: 'orangered' },
};

const PortalUserSelector = ({ roleName, portalDashboardPath }) => {
  const { t } = useTranslation('navigation');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [impersonatingId, setImpersonatingId] = useState(null);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-users', roleName, debouncedSearch],
    queryFn: () => UsersAPI.listUsers({
      role: roleName,
      search: debouncedSearch || undefined,
      filter: 'active',
      per_page: 20,
      page: 1,
    }),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const users = data?.items || [];
  const total = data?.total || 0;
  const config = PORTAL_CONFIG[roleName] || PORTAL_CONFIG.student;
  const portalName = t(`modules.${roleName}`);

  const handleEnter = async (user) => {
    setImpersonatingId(user.id);
    const result = await startImpersonation(user.id);
    if (result.success) {
      Message.success(tCommon('messages.success.impersonateStarted', { name: user.full_name || user.email }));
      navigate(portalDashboardPath || config.dashboardPath);
    } else {
      Message.error(result.error || tCommon('messages.error.saveError'));
      setImpersonatingId(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  };

  return (
    <div style={{ padding: '40px 24px', maxWidth: 700, margin: '0 auto' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <IconUser style={{ fontSize: 48, color: 'var(--color-text-3)', marginBottom: 12 }} />
          <Typography.Title heading={5} style={{ margin: 0 }}>
            Selecciona un usuario para ver {portalName}
          </Typography.Title>
          <Typography.Text type="secondary">
            Como administrador, puedes ver este portal impersonando a un usuario con rol{' '}
            <Tag size="small" color={config.color}>{roleName}</Tag>
          </Typography.Text>
        </div>

        <Input
          prefix={<IconSearch />}
          placeholder="Buscar por nombre o email..."
          value={searchText}
          onChange={setSearchText}
          allowClear
          size="large"
          style={{ marginBottom: 16 }}
        />

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size={30} />
          </div>
        ) : users.length === 0 ? (
          <Empty
            description={
              debouncedSearch
                ? `No se encontraron usuarios "${debouncedSearch}" con rol ${roleName}`
                : `No hay usuarios activos con rol ${roleName}`
            }
          />
        ) : (
          <>
            <List
              dataSource={users}
              render={(user) => (
                <List.Item
                  key={user.id}
                  style={{ padding: '12px 0' }}
                  actions={[
                    <Button
                      key="enter"
                      type="primary"
                      size="small"
                      icon={<IconEye />}
                      loading={impersonatingId === user.id}
                      disabled={impersonatingId !== null && impersonatingId !== user.id}
                      onClick={() => handleEnter(user)}
                    >
                      Entrar
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar size={40} style={{ backgroundColor: `var(--${config.color}-6, #165DFF)` }}>
                        {getInitials(user.full_name)}
                      </Avatar>
                    }
                    title={
                      <Space size={8}>
                        <span>{user.full_name || '(sin nombre)'}</span>
                        <Tag size="small" color="gray">ID {user.id}</Tag>
                      </Space>
                    }
                    description={user.email}
                  />
                </List.Item>
              )}
            />
            {total > users.length && (
              <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                Mostrando {users.length} de {total} usuarios — usa el buscador para filtrar
              </Typography.Text>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default PortalUserSelector;
